import React from 'react';
import { AlertTriangle, ArrowRight, CheckCircle, X } from 'lucide-react';
import { MOVE_JOB_WARNING_CODES, type MoveJobResult } from '../../lib/ticket-move-job';

/**
 * WHAT THE MOVE DID, after the modal has closed.
 *
 * A toast is the wrong shape for this. Three of the four things a move can
 * report are jobs for a person — read the customer notes, re-approve and
 * re-send, tell whoever lost delete authority — and a message that vanishes in
 * four seconds cannot carry a job. So it is a dismissible panel, it names the
 * two jobs, and it renders every warning the API sent, in the order it sent
 * them.
 *
 * A WARNING HERE IS NOT A FAILURE. The move happened; the panel is green-edged
 * and says so, and the warnings sit inside it as things to look at. Rendering
 * them as errors would make a completed move read as a refused one — which is
 * how an office user re-runs a move that already succeeded.
 */
export default function MoveTicketJobOutcome({
  result,
  ticketId,
  onDismiss,
}: {
  result: MoveJobResult;
  ticketId: string;
  onDismiss: () => void;
}) {
  const reverted = result.officeApprovalReverted;
  return (
    <div
      role="status"
      data-testid="move-job-outcome"
      className="rounded-xl border border-fieldiq-success/40 bg-fieldiq-success/10 p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <CheckCircle size={16} className="text-fieldiq-text mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-white">
              Moved {result.move.fromJobNumber ?? '—'} <ArrowRight size={12} className="inline align-middle" />{' '}
              {result.move.toJobNumber ?? '—'}
            </p>
            <p className="text-xs text-fieldiq-text mt-0.5">
              Line items, prices and the ticket&apos;s own well and rig details are unchanged. Reason recorded:{' '}
              <span className="text-white">&ldquo;{result.move.reason}&rdquo;</span>
            </p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss move result"
          className="p-1 rounded-lg text-fieldiq-muted hover:text-white flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>

      {/* THE OFFICE-APPROVAL REVERT GETS ITS OWN LINE, ahead of the warning
          list, because it is the one outcome that leaves a job half-done: the
          customer's link is dead and nobody outside this panel will say so. */}
      {reverted && (
        <div
          data-testid="move-job-reverted"
          className="rounded-lg border border-fi-amber/40 bg-fi-amber/10 p-3 flex items-start gap-2"
        >
          <AlertTriangle size={14} className="text-fieldiq-text mt-0.5 flex-shrink-0" />
          <p className="text-xs text-white">
            This ticket was <span className="font-semibold">office approved</span>. It is back at{' '}
            <span className="font-semibold">field approved</span>, the customer&apos;s approval link has been
            cancelled{reverted.certDaysReversed ? ' and its cert days were reversed' : ''}.{' '}
            <span className="font-semibold">Approve it again and send it to the customer.</span>
          </p>
        </div>
      )}

      {result.warnings.length > 0 && (
        <ul className="space-y-2" data-testid="move-job-warnings">
          {result.warnings
            /* The revert already has the block above. Printing the API's
               sentence as well would say the same thing twice, in two shapes,
               which reads as two separate problems. */
            .filter((w) => w.code !== MOVE_JOB_WARNING_CODES.officeApprovalReverted)
            .map((w) => (
              <li key={w.code} data-warning-code={w.code} className="flex items-start gap-2">
                <AlertTriangle size={13} className="text-fieldiq-text mt-0.5 flex-shrink-0" />
                <div className="text-xs text-white">
                  <p>{w.message}</p>
                  {/* The notes are free AI-written text and were NOT rewritten by
                      the move — on FT-04462 they named the old well in prose. Show
                      them here, because the office cannot judge the warning
                      without reading them, and link to where they are edited. */}
                  {w.code === MOVE_JOB_WARNING_CODES.customerNotes && result.customerNotes && (
                    <>
                      <blockquote className="mt-1 border-l-2 border-fieldiq-border pl-2 text-fieldiq-text">
                        {result.customerNotes.text}
                      </blockquote>
                      <a
                        href={`/tickets/${ticketId}/edit`}
                        className="underline underline-offset-2 text-white mt-1 inline-block"
                      >
                        Edit customer notes
                      </a>
                    </>
                  )}
                </div>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
